import { call, put, takeLatest, all } from 'redux-saga/effects';
import {
    fetchBoardListRequest,
    fetchBoardListSuccess,
    fetchBoardListFailure,
    uploadBoardRequest,
    uploadBoardSuccess,
    uploadBoardFailure,
    fetchBoardDetailRequest,
    fetchBoardDetailSuccess,
    fetchBoardDetailFailure,
    incrementViewCountRequest,
    incrementViewCountSuccess,
    incrementViewCountFailure,
    deleteBoardRequest,
    deleteBoardSuccess,
    deleteBoardFailure,
    reportBoardRequest,
    reportBoardSuccess,
    reportBoardFailure,
} from '../slice/boardSlice';

import {
    fetchBoards,
    uploadBoardApi,
    getBoardDetail,
    trackViewApi,
    deleteBoardApi,
    reportBoardApi,
} from '../../api/boardApi';

/* 파일/Blob-like 판별 */
function isFileLike(v: any): boolean {
    return !!(v && typeof v === 'object' && typeof (v as any).name === 'string' && typeof (v as any).size === 'number');
}

/* workers */

// 목록
function* fetchBoardListWorker(): Generator<any, void, any> {
    try {
        const data = yield call(fetchBoards);
        // data는 배열 또는 { pinned,trending,normal } 객체일 수 있다.
        yield put(fetchBoardListSuccess(data));
    } catch (err: any) {
        yield put(fetchBoardListFailure(err?.message ?? '게시글 목록 로드 실패'));
    }
}

// 업로드
function* uploadBoardWorker(action: ReturnType<typeof uploadBoardRequest>): Generator {
    try {
        const { title, content, imageUrl } = action.payload;

        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);

        if (isFileLike(imageUrl)) {
            formData.append('image', imageUrl as any);
        } else if (typeof imageUrl === 'string' && imageUrl.trim()) {
            formData.append('imageUrl', imageUrl.trim());
        }

        yield call(uploadBoardApi, formData);
        yield put(uploadBoardSuccess());

        // 업로드 후 목록 최신화
        yield call(fetchBoardListWorker);
    } catch (err: any) {
        yield put(uploadBoardFailure(err?.message ?? '게시글 업로드 실패'));
    }
}

// 상세 + 조회수
function* fetchBoardDetailWorker(action: ReturnType<typeof fetchBoardDetailRequest>): Generator<any, void, any> {
    try {
        const id = action.payload;
        const row = yield call(getBoardDetail, id);
        yield put(fetchBoardDetailSuccess(row));
        // 성공 후 조회수 증가 트래킹
        yield put(incrementViewCountRequest(id));
    } catch (err: any) {
        yield put(fetchBoardDetailFailure(err?.message ?? '게시글 상세 로드 실패'));
    }
}

function* incrementViewCountWorker(action: ReturnType<typeof incrementViewCountRequest>): Generator {
    try {
        yield call(trackViewApi, action.payload);
        yield put(incrementViewCountSuccess(action.payload));
    } catch (err: any) {
        yield put(incrementViewCountFailure(err?.message ?? '조회수 반영 실패'));
    }
}

// 삭제
function* deleteBoardWorker(action: ReturnType<typeof deleteBoardRequest>): Generator {
    try {
        const id = action.payload;
        yield call(deleteBoardApi, id);
        yield put(deleteBoardSuccess(id));
        // 삭제 후 목록 재조회
        yield call(fetchBoardListWorker);
    } catch (err: any) {
        yield put(deleteBoardFailure(err?.message ?? '게시글 삭제 실패'));
    }
}

// 신고
function* reportBoardWorker(action: ReturnType<typeof reportBoardRequest>): Generator {
    try {
        const { id, reason } = action.payload;
        yield call(reportBoardApi, { id, reason });
        yield put(reportBoardSuccess());
    } catch (err: any) {
        yield put(reportBoardFailure(err?.message ?? '신고 실패'));
    }
}

/* watchers */
function* watchFetchBoardList() { yield takeLatest(fetchBoardListRequest.type, fetchBoardListWorker); }
function* watchUploadBoard() { yield takeLatest(uploadBoardRequest.type, uploadBoardWorker); }
function* watchFetchBoardDetail() { yield takeLatest(fetchBoardDetailRequest.type, fetchBoardDetailWorker); }
function* watchIncrementView() { yield takeLatest(incrementViewCountRequest.type, incrementViewCountWorker); }
function* watchDeleteBoard() { yield takeLatest(deleteBoardRequest.type, deleteBoardWorker); }
function* watchReportBoard() { yield takeLatest(reportBoardRequest.type, reportBoardWorker); }

/* root saga */
export default function* boardSagas() {
    yield all([
        watchFetchBoardList(),
        watchUploadBoard(),
        watchFetchBoardDetail(),
        watchIncrementView(),
        watchDeleteBoard(),
        watchReportBoard(),
    ]);
}
